import * as THREE from 'three';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';

/**
 * Utility class for loading and configuring textures
 */
export class TextureLoader {
    constructor(renderer = null) {
        this.loader = new THREE.TextureLoader();
        this.ktx2Loader = null;

        // Initialize KTX2 loader if renderer is provided
        if (renderer) {
            this.initializeKTX2Loader(renderer);
        }
    }

    /**
     * Initialize KTX2 loader with renderer support
     * @param {THREE.WebGLRenderer} renderer - Three.js renderer
     */
    initializeKTX2Loader(renderer) {
        this.ktx2Loader = new KTX2Loader();
        // Set path to basis transcoder files using Vite base URL (relative-friendly)
        // import.meta.env.BASE_URL est résolu à la base (ex: './' en app build)
        const base = (import.meta && import.meta.env && import.meta.env.BASE_URL) || '/';
        const path = `${base}libs/basis/`;
        this.ktx2Loader.setTranscoderPath(path);
        this.ktx2Loader.detectSupport(renderer);

        // Log GPU compression support
        const gl = renderer.getContext();
        console.log('✅ KTX2Loader initialized with basis transcoder support');
        console.log('📊 GPU Compression Support:', {
            s3tc: gl.getExtension('WEBGL_compressed_texture_s3tc') !== null,
            etc1: gl.getExtension('WEBGL_compressed_texture_etc1') !== null,
            etc: gl.getExtension('WEBGL_compressed_texture_etc') !== null,
            astc: gl.getExtension('WEBGL_compressed_texture_astc') !== null,
            bptc: gl.getExtension('EXT_texture_compression_bptc') !== null,
            pvrtc: gl.getExtension('WEBGL_compressed_texture_pvrtc') !== null
        });
    }

    /**
     * Load an image file as a Three.js texture configured for 360° viewing
     * @param {string} url - URL or blob URL of the image
     * @param {number} scale - Optional scale factor (e.g., 0.66 for 1.5x downscale) - DEBUG ONLY
     * @returns {Promise<THREE.Texture>}
     */
    async load360Texture(url, scale = 1.0) {
        // Check if file is KTX2 format
        const isKTX2 = url.toLowerCase().endsWith('.ktx2');

        if (isKTX2 && this.ktx2Loader) {
            // Use KTX2 loader for compressed textures
            return this.loadKTX2Texture(url);
        }

        // Use standard loader for regular images
        return new Promise((resolve, reject) => {
            this.loader.load(
                url,
                (texture) => {
                    // DEBUG: Downscale texture if requested
                    if (scale < 1.0) {
                        const img = texture.image;
                        const canvas = document.createElement('canvas');
                        canvas.width = Math.floor(img.width * scale);
                        canvas.height = Math.floor(img.height * scale);
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                        texture = new THREE.Texture(canvas);
                        texture.needsUpdate = true;
                    }

                    // Configure texture for 360° viewing
                    this.configure360Texture(texture);

                    resolve(texture);
                },
                undefined,
                reject
            );
        });
    }

    /**
     * Load a KTX2 compressed texture for 360° viewing
     * @param {string} url - URL of the KTX2 file
     * @returns {Promise<THREE.Texture>}
     */
    async loadKTX2Texture(url) {
        if (!this.ktx2Loader) {
            throw new Error('KTX2Loader not initialized. Pass renderer to TextureLoader constructor.');
        }

        return new Promise((resolve, reject) => {
            this.ktx2Loader.load(
                url,
                (texture) => {
                    console.log(`✅ KTX2 texture loaded: ${url}`);
                    console.log(`   Texture format:`, texture.format, '(transcoded)');
                    console.log(`   Texture type:`, texture.type);
                    console.log(`   Texture dimensions:`, texture.image?.width, 'x', texture.image?.height);
                    console.log(`   Texture.image:`, texture.image);
                    console.log(`   Has mipmaps:`, texture.mipmaps?.length);

                    // For compressed textures, mipmaps contain the actual data
                    if (texture.mipmaps && texture.mipmaps.length > 0) {
                        console.log(`   Mipmap[0] data:`, texture.mipmaps[0].data?.byteLength, 'bytes');
                    }

                    // Compressed textures don't have .image.data, they use mipmaps
                    // This is normal for KTX2/compressed textures

                    // Configure texture for 360° viewing (KTX2 version)
                    this.configure360TextureKTX2(texture);

                    resolve(texture);
                },
                undefined,
                (error) => {
                    console.error(`❌ Failed to load KTX2 texture: ${url}`, error);
                    reject(error);
                }
            );
        });
    }

    /**
     * Configure texture settings for 360° viewing (KTX2 compressed textures)
     * KTX2 textures don't support flipY, so we handle orientation differently
     * @param {THREE.Texture} texture - Texture to configure
     */
    configure360TextureKTX2(texture) {
        // DEBUG: Log texture info
        console.log('[TextureLoader] KTX2 Texture info BEFORE config:', {
            format: texture.format,
            type: texture.type,
            width: texture.image?.width,
            height: texture.image?.height,
            colorSpace: texture.colorSpace,
            flipY: texture.flipY,
            minFilter: texture.minFilter,
            magFilter: texture.magFilter,
            mipmaps: texture.mipmaps?.length,
            isCompressedTexture: texture.isCompressedTexture,
            wrapS: texture.wrapS,
            wrapT: texture.wrapT
        });

        // IMPORTANT: Some properties must be set before the texture is used

        // Wrapping mode - MUST be set first
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;

        // IMPORTANT: KTX2/compressed textures don't support flipY
        texture.flipY = false;

        // Don't override colorSpace - use what KTX2Loader set
        // (it's usually correct based on the file metadata)

        // Compressed textures don't generate mipmaps - they use built-in ones
        texture.generateMipmaps = false;

        // Use appropriate filters based on whether we have mipmaps
        if (texture.mipmaps && texture.mipmaps.length > 1) {
            texture.minFilter = THREE.LinearMipmapLinearFilter;
            console.log(`   Using mipmap filter (${texture.mipmaps.length} levels)`);
        } else {
            texture.minFilter = THREE.LinearFilter;
            console.log(`   Using linear filter (no mipmaps)`);
        }
        texture.magFilter = THREE.LinearFilter;

        // Fix horizontal orientation for 360° images
        // These transformations are applied in the shader
        texture.offset.x = 0.5;
        texture.repeat.x = -1;

        // CRITICAL: Mark texture for update
        texture.needsUpdate = true;

        console.log('[TextureLoader] KTX2 Texture info AFTER config:', {
            wrapS: texture.wrapS,
            wrapT: texture.wrapT,
            colorSpace: texture.colorSpace,
            minFilter: texture.minFilter,
            magFilter: texture.magFilter,
            offset: { x: texture.offset.x, y: texture.offset.y },
            repeat: { x: texture.repeat.x, y: texture.repeat.y },
            needsUpdate: texture.needsUpdate,
            flipY: texture.flipY
        });
    }

    /**
     * Configure texture settings for 360° viewing
     * @param {THREE.Texture} texture - Texture to configure
     */
    configure360Texture(texture) {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.flipY = true;
        texture.colorSpace = THREE.SRGBColorSpace;

        // Fix horizontal orientation for 360° images
        texture.offset.x = 0.5;
        texture.repeat.x = -1;

        // Disable mipmaps for faster GPU upload (zoom quality slightly reduced)
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
    }

    /**
     * Load a standard texture (for hotspots, masks, etc.)
     * @param {string} url - URL or blob URL of the image
     * @returns {Promise<THREE.Texture>}
     */
    async loadStandardTexture(url) {
        return new Promise((resolve, reject) => {
            this.loader.load(
                url,
                (texture) => {
                    texture.colorSpace = THREE.SRGBColorSpace;
                    resolve(texture);
                },
                undefined,
                reject
            );
        });
    }

    /**
     * Create a clean texture copy suitable for flat images (hotspots)
     * @param {THREE.Texture} sourceTexture - Source texture to clone
     * @returns {THREE.Texture}
     */
    createFlatTexture(sourceTexture) {
        const texture = sourceTexture.clone();
        texture.offset.x = 0;
        texture.repeat.x = 1;
        texture.flipY = true;
        return texture;
    }
}

